import { Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';

import { AuthErrorBoundary } from '@/components/auth';
import { TermsAcceptanceGate } from '@/components/auth/TermsAcceptanceGate';
import { ScrollToTop } from '@/components/common/ScrollToTop';
import { DevTools } from '@/components/dev/DevTools';
import { SetupMissingTranslationReporter } from '@/components/dev/SetupMissingTranslationReporter';
import { RouteErrorBoundary } from '@/components/errors';
import { ReportDialogHost } from '@/components/moderation/ReportDialog';

import { usePageTracking } from '@/hooks/usePageTracking';

import { initGA } from '@/lib/analytics';

// Static pages
import { AboutPage } from './pages/AboutPage';
import { AccountDeletionPage } from './pages/AccountDeletionPage';
import ActivityFeedPage from './pages/ActivityFeedPage';
// Page imports
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import ClaimLinkPage from './pages/ClaimLinkPage';
import { ContactPage } from './pages/ContactPage';
import ExpenseCreatePage from './pages/ExpenseCreatePage';
import ExpenseDetailPage from './pages/ExpenseDetailPage';
import ExpenseEditPage from './pages/ExpenseEditPage';
import { FAQPage } from './pages/FAQPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import GroupActivityPage from './pages/GroupActivityPage';
import GroupBalancesPage from './pages/GroupBalancesPage';
import GroupCreatePage from './pages/GroupCreatePage';
import GroupDetailPage from './pages/GroupDetailPage';
import GroupEditPage from './pages/GroupEditPage';
import GroupImportPage from './pages/GroupImportPage';
import GroupMembersPage from './pages/GroupMembersPage';
import GroupRecurringRulesPage from './pages/GroupRecurringRulesPage';
import GroupReportsPage from './pages/GroupReportsPage';
import GroupSettlementsPage from './pages/GroupSettlementsPage';
import GroupsListPage from './pages/GroupsListPage';
import InvitePage from './pages/InvitePage';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { LogoutPage } from './pages/LogoutPage';
import NotificationsPage from './pages/NotificationsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import ProfilePage from './pages/ProfilePage';
import { RegisterPage } from './pages/RegisterPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import SettingsPage from './pages/SettingsPage';
import { TermsPage } from './pages/TermsPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { Providers } from './providers';

// Initialize Google Analytics (only loads in production)
initGA();

export function App() {
  // Track page views in Google Analytics (only in production)
  usePageTracking();

  return (
    <Providers>
      <ScrollToTop />
      <SetupMissingTranslationReporter />
      <DevTools />
      <RouteErrorBoundary data-testid="route-error-boundary">
        <AuthErrorBoundary data-testid="auth-error-boundary">
          {/* Post-OAuth ToS/Privacy gate — blocking overlay for signed-in
              users without a recorded acceptance (store compliance). */}
          <TermsAcceptanceGate />
          {/* App-wide UGC report dialog — one instance, opened
              imperatively from any report affordance. */}
          <ReportDialogHost />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/logout" element={<LogoutPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/invite/:token" element={<InvitePage />} />
            <Route path="/c/:token" element={<ClaimLinkPage />} />

            {/* Static pages */}
            <Route path="/about" element={<AboutPage />} />
            {/* Public: Google Data-safety info page + emailed-token landing */}
            <Route path="/account/deletion" element={<AccountDeletionPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FAQPage />} />

            {/* Authenticated routes */}
            <Route path="/activity" element={<ActivityFeedPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />

            {/* Redirect old /home route to /groups */}
            <Route path="/home" element={<Navigate to="/groups" replace />} />

            {/* Groups routes */}
            <Route path="/groups" element={<GroupsListPage />} />
            <Route path="/groups/create" element={<GroupCreatePage />} />
            {/* Before `/groups/:id` so the static segment wins the match. */}
            <Route path="/groups/import" element={<GroupImportPage />} />
            <Route path="/groups/:id" element={<GroupDetailPage />} />
            <Route
              path="/groups/:id/activity"
              element={<GroupActivityPage />}
            />
            <Route
              path="/groups/:id/balances"
              element={<GroupBalancesPage />}
            />
            <Route path="/groups/:id/edit" element={<GroupEditPage />} />
            <Route path="/groups/:id/members" element={<GroupMembersPage />} />
            <Route
              path="/groups/:id/recurring"
              element={<GroupRecurringRulesPage />}
            />
            <Route path="/groups/:id/reports" element={<GroupReportsPage />} />
            <Route
              path="/groups/:id/settlements"
              element={<GroupSettlementsPage />}
            />

            {/* Expense routes */}
            <Route
              path="/groups/:id/expenses/create"
              element={<ExpenseCreatePage />}
            />
            <Route
              path="/groups/:id/expenses/:expenseId"
              element={<ExpenseDetailPage />}
            />
            <Route
              path="/groups/:id/expenses/:expenseId/edit"
              element={<ExpenseEditPage />}
            />

            {/* 404 */}
            <Route path="*" element={<div>404 - Page Not Found</div>} />
          </Routes>
        </AuthErrorBoundary>
      </RouteErrorBoundary>
      <Toaster
        position="top-center"
        richColors
        closeButton
        duration={5000}
        toastOptions={{
          style: {
            background: 'var(--toast-bg)',
            color: 'var(--toast-color)',
            border: 'var(--toast-border)',
          },
          className: 'sonner-toast',
        }}
      />
    </Providers>
  );
}
